import { lazy, Suspense, useState, useEffect, useRef, useCallback } from "react";
import { Button, CircularProgress, MenuItem, TextField, Typography } from "@material-ui/core";
import { api } from "../../services/apiService";
import { SurgeryStudyResources } from "../../components/question-resources/question-resources";
import { CONSTANTS } from "../../constants/app-constant";
import { QUESTION_SELECTIONS, FRESH_QUESTION_HELP, practicePoolError } from "./student-practice-options";
import PracticeSuggestions from "./practice-suggestions";

// Tutor-led practice ("Do together") with explicit student coverage tracking. The tutor hosts this on their own
// screen (shared over Zoom) and leads the student through a few questions:
// choices, an EXPLICIT reveal, explanation, navigation, reset.
//
// Deliberately isolated from ExamQuestionContainer / exam.jsx: those are wired
// to the student's answer/submit/activity sagas, and this surface must be
// unable to write independent scores. Question reads and explicit coverage saves
// use mentorship endpoints. Answers and reveals stay in React state (no redux,
// storage or polling), for ONE studentUserId; the page remounts via `key`.
//
// Optional SAVED mode (`preloaded` + `onFinish`, used by the tutor's post-session
// quiz rows): the student's actual quiz arrives as a prop, so there is no entry
// button and no picker, and "End" becomes "Finish and save for <student>".
// In saved mode it hands the selections map to
// onFinish (resolves true on success) and only shows a retry when that fails.

// The shared, stateless explanation renderer (fetches nothing). Loaded lazily
// so the markdown stack only enters the student page when a tutor actually
// reveals an answer — the flag-off / non-tutor page imports nothing new.
const DetailedGuidanceContent = lazy(() =>
  import("../../components/detailed-guidance-content/detailed-guidance-content"));

export const GUIDED_PRACTICE_MAX_QUESTIONS = 20;
const DEFAULT_QUESTION_COUNT = 5;
export const GUIDED_PRACTICE_NOTICE =
  "Mark questions covered to remember them for this student. Practice answers do not affect student scores.";

const clampCount = (value) =>
  Math.min(GUIDED_PRACTICE_MAX_QUESTIONS, Math.max(1, Math.round(Number(value)) || DEFAULT_QUESTION_COUNT));

const sameAnswer = (a, b) => `${a ?? ""}`.trim().toLowerCase() === `${b ?? ""}`.trim().toLowerCase();

// api.getImage resolves { imgData } where QBankManager builds
// `data:image/<file extension>;base64,<bytes>` (extension verbatim, so "JPG"
// and "jpg" both occur). Only that exact shape, for raster types, may become an
// <img src>: anything else (remote/javascript: URLs, other MIME types, SVG,
// stray markup, non-strings) is a load FAILURE, never rendered.
const SAFE_IMAGE_PREFIX = /^data:image\/(png|jpe?g|gif|webp|bmp);base64,/i;
const BASE64_BODY = /^[A-Za-z0-9+/]+={0,2}$/;

export const safeImageDataUrl = (imgData) => {
  if (typeof imgData !== "string") return null;
  const match = SAFE_IMAGE_PREFIX.exec(imgData);
  if (!match) return null;
  return BASE64_BODY.test(imgData.slice(match[0].length)) ? imgData : null;
};

const imageNamesOf = (q) =>
  Array.isArray(q?.imageList)
    ? [...new Set(q.imageList.filter((n) => typeof n === "string" && n.trim()))]
    : [];

// doBodyCall resolves a failure to { errStatus, errMsg } where errMsg is the
// PARSED error body: an object ({status, message}) for backend exceptions, a
// fixed string for a network reject. The server's words are only CLASSIFIED
// against the validation shapes this endpoint is known to produce and mapped
// to our own copy — an unrecognised body is never shown on a shared screen.
const GENERIC_LOAD_ERROR = "Couldn't load practice questions. Please try again.";

const serverReason = (res) => {
  const raw = res?.errMsg;
  if (typeof raw === "string") return raw;
  return raw && typeof raw.message === "string" ? raw.message : "";
};

export const guidedLoadErrorMessage = (res) => {
  const status = Number(res?.errStatus);
  const reason = serverReason(res);
  if (status === 403) {
    if (/not included in the student's plan/i.test(reason)) {
      return "This exam isn't included in the student's plan. Choose a different exam.";
    }
    if (/no active mentorship/i.test(reason)) {
      return "You don't have an active link with permission to practice with this student.";
    }
    // Cause unknown: never assert a missing mentorship when it may be the plan.
    return "You can't practice this with the student right now: your link may no longer be active, or this exam isn't in the student's plan.";
  }
  if (status === 404) return "Practice together isn't available right now.";
  if (status === 400) {
    const poolMessage = practicePoolError(res);
    if (poolMessage) return poolMessage;
    if (/insufficient questions/i.test(reason)) {
      return "Not enough questions match these filters. Choose fewer questions, broaden topics, or include previous questions.";
    }
    if (/noOfQuestions must be between/i.test(reason)) {
      return `Choose between 1 and ${GUIDED_PRACTICE_MAX_QUESTIONS} questions.`;
    }
    if (/(category|exam type) must be selected/i.test(reason)) {
      return "Choose an exam and at least one topic, then try again.";
    }
    return "These filters couldn't be used. Adjust them and try again.";
  }
  return GENERIC_LOAD_ERROR;
};

const hasAnswerKey = (q) => `${q?.correctAnswer ?? ""}`.trim() !== "";

const isRenderableQuestion = (q) =>
  q && typeof q.id === "string" && typeof q.stem === "string" && Array.isArray(q.optionList);

const usableQuestions = (list) => (Array.isArray(list) ? list.filter(isRenderableQuestion) : []);

const FINISH_ERROR = "Couldn't save the quiz. The answers are still here — try again.";

export default function GuidedPracticeSection({ studentUserId, studentName, quizHistory = [], preloaded, onFinish }) {
  const preloadedQuizId = preloaded?.quizId;
  const [open, setOpen] = useState(!!preloaded);
  const [examType, setExamType] = useState(CONSTANTS.examTypeList?.[0]?.value || "ABSITE");
  const [qCount, setQCount] = useState(DEFAULT_QUESTION_COUNT);
  const [selectedDisciplines, setSelectedDisciplines] = useState([]);
  const [selectedOrgSystems, setSelectedOrgSystems] = useState([]);
  const [catLists, setCatLists] = useState({ discipline: [], organSystem: [] });
  const [catsLoading, setCatsLoading] = useState(false);
  const [questionSelection, setQuestionSelection] = useState("FRESH");
  const [coverage, setCoverage] = useState({});
  const coveragePending = useRef(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [questions, setQuestions] = useState(() => (preloaded ? usableQuestions(preloaded.questions) : null));
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState("");
  const [index, setIndex] = useState(0);
  const [selections, setSelections] = useState({});
  const [revealed, setRevealed] = useState({});
  // Question figures for THIS session only, keyed by image name:
  // { status: "loading" | "ready" | "error", src? }. Keying by name means a
  // slow reply can only ever fill its own slot — it cannot appear under a
  // question navigated to meanwhile — and back-navigation needs no refetch.
  const [images, setImages] = useState({});

  // Monotonic request token: bumping it orphans every in-flight response, so a
  // slow reply for a previous student (or after unmount/End) can never land.
  const requestSeq = useRef(0);
  // Focus follows a DELIBERATE reveal only: the click handler arms this flag,
  // the effect below consumes it once. Revisiting an already-revealed question,
  // Reset, image loads or any other re-render never move focus.
  const revealPanelRef = useRef(null);
  const focusRevealPanel = useRef(false);

  const clearSession = useCallback(() => {
    requestSeq.current += 1;
    setQuestions(null);
    setIndex(0);
    setSelections({});
    setRevealed({});
    setImages({});
    setCoverage({});
    coveragePending.current.clear();
    setLoading(false);
    setError("");
  }, []);

  const loadImage = useCallback(async (name) => {
    // Session token: End / New questions / student change / unmount bump it,
    // so a reply from an earlier session is dropped instead of being cached.
    const seq = requestSeq.current;
    setImages((prev) => ({ ...prev, [name]: { status: "loading" } }));
    let src = null;
    try {
      // Existing authenticated READ (same call the exam screen's saga makes);
      // resolves { imgData }, or { status, errMsg } / an Error on failure.
      const res = await api.getImage(name);
      src = safeImageDataUrl(res?.imgData);
    } catch {
      src = null;
    }
    if (seq !== requestSeq.current) return;
    setImages((prev) => ({ ...prev, [name]: src ? { status: "ready", src } : { status: "error" } }));
  }, []);

  // Never keep another student's context: any student change returns to the
  // closed entry state. Unmount orphans in-flight requests the same way.
  useEffect(() => {
    // A preloaded quiz IS the session: the parent remounts per quiz via `key`.
    if (!preloadedQuizId) {
      clearSession();
      setOpen(false);
      setSelectedDisciplines([]);
      setSelectedOrgSystems([]);
      setQuestionSelection("FRESH");
    }
    return () => {
      requestSeq.current += 1;
    };
  }, [studentUserId, preloadedQuizId, clearSession]);

  // Fetch the CURRENT question's figures once. A failed image is never
  // retried automatically (no loops) — the tutor retries explicitly.
  const currentQuestion = questions ? questions[index] : null;
  useEffect(() => {
    imageNamesOf(currentQuestion).forEach((name) => {
      if (!images[name]) loadImage(name);
    });
  }, [currentQuestion, images, loadImage]);

  useEffect(() => {
    if (!focusRevealPanel.current) return;
    focusRevealPanel.current = false;
    if (revealPanelRef.current) revealPanelRef.current.focus();
  }, [revealed]);

  const catsReady = catLists.discipline.length > 0 && catLists.organSystem.length > 0;

  const loadCategories = async () => {
    const seq = requestSeq.current;
    setCatsLoading(true);
    let lists = { discipline: [], organSystem: [] };
    try {
      const res = await api.getCategories();
      lists = {
        discipline: Array.isArray(res?.discipline) ? res.discipline : [],
        organSystem: Array.isArray(res?.organSystem) ? res.organSystem : [],
      };
    } catch (e) {
      // degraded to empty lists below — Start stays disabled
    }
    if (seq !== requestSeq.current) return;
    setCatLists(lists);
    setCatsLoading(false);
  };

  const onOpen = () => {
    clearSession();
    setOpen(true);
    if (!catsReady) loadCategories();
  };

  const onEnd = () => {
    clearSession();
    setOpen(false);
  };

  const onStart = async () => {
    // Drop whatever was on screen first: if this load is denied (link revoked
    // mid-session) nothing from the earlier set may stay revealable.
    clearSession();
    const seq = requestSeq.current;
    setLoading(true);
    let next = null;
    let message = "";
    try {
      // Same category semantics as the assign dialog: always FULL arrays
      // ("whole exam" = the full list, never an omitted key or []).
      const res = await api.getGuidedPracticeQuestions(studentUserId, {
        noOfQuestions: clampCount(qCount),
        questionSelection,
        categoryMap: {
          examType: [examType],
          discipline: selectedDisciplines.length ? [...selectedDisciplines] : [...catLists.discipline],
          organSystem: selectedOrgSystems.length ? [...selectedOrgSystems] : [...catLists.organSystem],
        },
      });
      const usable = usableQuestions(res);
      if (usable.length > 0) {
        next = usable.slice(0, GUIDED_PRACTICE_MAX_QUESTIONS);
      } else {
        message = guidedLoadErrorMessage(res);
      }
    } catch {
      message = GENERIC_LOAD_ERROR;
    }
    if (seq !== requestSeq.current) return;
    setLoading(false);
    if (next) {
      setQuestions(next);
    } else {
      setQuestions(null);
      setError(message);
    }
  };

  const markCovered = async (questionId) => {
    if (coveragePending.current.has(questionId) || coverage[questionId] === "saved") return;
    const seq = requestSeq.current;
    coveragePending.current.add(questionId);
    setCoverage(prev => ({ ...prev, [questionId]: "saving" }));
    let saved = false;
    try {
      const result = await api.markGuidedPracticeCovered(studentUserId, questionId);
      saved = result?.covered === true && result?.questionId === questionId;
    } catch { /* Keep the question open with an explicit retry. */ }
    if (seq !== requestSeq.current) return;
    coveragePending.current.delete(questionId);
    setCoverage(prev => ({ ...prev, [questionId]: saved ? "saved" : "error" }));
  };

  const onReset = () => {
    setIndex(0);
    setSelections({});
    setRevealed({});
  };

  const onFinishClick = async () => {
    const seq = requestSeq.current;
    setFinishing(true);
    setFinishError("");
    let saved = false;
    try {
      saved = (await onFinish({ ...selections })) === true;
    } catch {
      // A thrown save is the same as a false one: keep the answers, show retry.
      saved = false;
    }
    // On success the parent unmounts this section, which bumps the token.
    if (seq !== requestSeq.current) return;
    setFinishing(false);
    if (!saved) setFinishError(FINISH_ERROR);
  };

  if (!open) {
    return (
      <div className="gp-section" data-testid="gp-section">
        <Button
          variant="outlined"
          color="primary"
          size="small"
          className="gp-open-btn"
          data-testid="gp-open"
          onClick={onOpen}
        >
          Do together
        </Button>
      </div>
    );
  }

  const current = questions ? questions[index] || null : null;
  const who = studentName || "the student";
  const chosen = current ? selections[current.id] : undefined;
  const isRevealed = current ? !!revealed[current.id] : false;
  const imageNames = imageNamesOf(current);
  const failedImages = imageNames.filter((name) => images[name]?.status === "error");
  // A question that depends on a figure is incomplete without it: reveal stays
  // off until every one of its images is actually on screen.
  const imagesReady = imageNames.every((name) => images[name]?.status === "ready");

  return (
    <section
      className="gp-section gp-open"
      data-testid="gp-section"
      aria-label={studentName ? `Practice together with ${studentName}` : "Practice together"}
    >
      <Typography variant="h6" component="h2" className="gp-title">
        Do together{studentName ? ` — ${studentName}` : ""}
      </Typography>
      {preloaded?.quizTitle && <p className="gp-quiz-title">{preloaded.quizTitle}</p>}
      <p className="gp-notice" role="status" data-testid="gp-notice">
        {preloaded ? `This saves the quiz as completed on ${who}'s account.` : GUIDED_PRACTICE_NOTICE}
      </p>

      {error && (
        <p className="gp-error" role="alert" data-testid="gp-error">
          {error}
        </p>
      )}

      {!current && preloaded && (
        <p className="gp-error" role="alert" data-testid="gp-error">
          This quiz's questions couldn't be loaded. Close it and try again.
        </p>
      )}

      {!current && !preloaded && (
        <div className="gp-picker">
          <TextField
            select
            id="gp-exam"
            label="Exam"
            value={examType}
            onChange={(e) => setExamType(e.target.value)}
            size="small"
            variant="outlined"
            inputProps={{ "data-testid": "gp-exam" }}
          >
            {(CONSTANTS.examTypeList || []).map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label || o.value}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Questions" value={questionSelection}
            onChange={e => setQuestionSelection(e.target.value)} size="small" variant="outlined"
            helperText={questionSelection === "FRESH" ? FRESH_QUESTION_HELP : "Previous questions are included intentionally."}
            inputProps={{ "data-testid": "gp-question-selection" }}>
            {QUESTION_SELECTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </TextField>
          {/* null history = the page is still loading it: no empty-state claim yet. */}
          {catsReady && Array.isArray(quizHistory) && (
            <PracticeSuggestions history={quizHistory} exam={examType} catalog={catLists.discipline}
              questionSelection={questionSelection}
              onUseTopics={(topics) => { setSelectedDisciplines(topics); setSelectedOrgSystems([]); }}
              onReviewMissed={() => setQuestionSelection("INCORRECT")} />
          )}
          <TextField
            id="gp-count"
            label="Number of questions"
            type="number"
            value={qCount}
            onChange={(e) => setQCount(e.target.value)}
            onBlur={() => setQCount(clampCount(qCount))}
            size="small"
            variant="outlined"
            helperText={`Up to ${GUIDED_PRACTICE_MAX_QUESTIONS}`}
            inputProps={{ min: 1, max: GUIDED_PRACTICE_MAX_QUESTIONS, "data-testid": "gp-count" }}
          />
          <TextField
            select
            id="gp-disciplines"
            label="Disciplines (optional)"
            value={selectedDisciplines}
            onChange={(e) => setSelectedDisciplines(e.target.value)}
            size="small"
            variant="outlined"
            disabled={!catsReady}
            SelectProps={{ multiple: true }}
            inputProps={{ "data-testid": "gp-discipline" }}
          >
            {catLists.discipline.map((d) => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            id="gp-organ-systems"
            label="Organ systems (optional)"
            value={selectedOrgSystems}
            onChange={(e) => setSelectedOrgSystems(e.target.value)}
            size="small"
            variant="outlined"
            disabled={!catsReady}
            SelectProps={{ multiple: true }}
            inputProps={{ "data-testid": "gp-orgsystem" }}
          >
            {catLists.organSystem.map((o) => (
              <MenuItem key={o} value={o}>{o}</MenuItem>
            ))}
          </TextField>
          {examType === "ABSITE" && <SurgeryStudyResources />}
          {!catsLoading && !catsReady && (
            <p className="gp-error" data-testid="gp-cats-error">
              Couldn't load topics. Close and reopen to try again.
            </p>
          )}
          <div className="gp-actions">
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={onStart}
              disabled={loading || catsLoading || !catsReady}
              data-testid="gp-start"
              aria-label="Start practice"
            >
              {loading ? <CircularProgress size={18} color="inherit" /> : "Start practice"}
            </Button>
            <Button size="small" onClick={onEnd} data-testid="gp-cancel">Close</Button>
          </div>
        </div>
      )}

      {current && (
        <div className="gp-question" data-testid="gp-question">
          <p className="gp-progress" aria-live="polite">
            Question {index + 1} of {questions.length}
          </p>
          <p className="gp-stem" id={`gp-stem-${index}`}>{current.stem}</p>
          {imageNames.length > 0 && (
            <div className="gp-images" data-testid="gp-images">
              {imageNames.map((name, i) => {
                const entry = images[name];
                if (entry?.status === "ready") {
                  return (
                    <img
                      key={name}
                      className="gp-image"
                      src={entry.src}
                      alt={`Figure ${i + 1} of ${imageNames.length} for question ${index + 1}, referenced by the question stem`}
                    />
                  );
                }
                if (entry?.status === "error") return null;
                return (
                  <div key={name} className="gp-hint gp-image-loading" aria-live="polite">
                    <CircularProgress size={14} /> Loading image {i + 1} of {imageNames.length}…
                  </div>
                );
              })}
              {failedImages.length > 0 && (
                <div className="gp-error gp-image-error" role="alert" data-testid="gp-image-error">
                  <p>
                    {failedImages.length === 1 ? "An image" : `${failedImages.length} images`} for this
                    question couldn't be loaded. This question needs its image, so the answer stays
                    hidden until it loads — retry, or move to another question.
                  </p>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => failedImages.forEach((name) => loadImage(name))}
                    aria-label="Retry image"
                  >
                    Retry image
                  </Button>
                </div>
              )}
            </div>
          )}
          <fieldset
            className="gp-choices"
            role="radiogroup"
            aria-label="Answer choices"
            aria-describedby={`gp-stem-${index}`}
          >
            {current.optionList.map((option, i) => {
              const optionId = `gp-opt-${index}-${i}`;
              const isKey = isRevealed && hasAnswerKey(current) && sameAnswer(option, current.correctAnswer);
              return (
                <div key={optionId} className={`gp-choice${isKey ? " gp-choice-correct" : ""}`}>
                  <input
                    type="radio"
                    id={optionId}
                    name={`gp-q-${current.id}`}
                    value={option}
                    checked={chosen === option}
                    onChange={() => setSelections((prev) => ({ ...prev, [current.id]: option }))}
                  />
                  <label htmlFor={optionId}>
                    <span className="gp-choice-letter">{String.fromCodePoint(65 + i)}.</span> {option}
                    {isKey && <span className="gp-choice-tag"> (correct answer)</span>}
                  </label>
                </div>
              );
            })}
          </fieldset>

          {!isRevealed && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              data-testid="gp-reveal"
              disabled={!imagesReady}
              onClick={() => {
                focusRevealPanel.current = true;
                setRevealed((prev) => ({ ...prev, [current.id]: true }));
              }}
            >
              Reveal answer
            </Button>
          )}

          {isRevealed && (
            <section
              className="gp-reveal-panel exam-review-drawer-content"
              data-testid="gp-reveal-panel"
              ref={revealPanelRef}
              tabIndex={-1}
              aria-label={`Answer and explanation for question ${index + 1}`}
            >
              {/* No verdict without a key: a missing key must never read as "wrong". */}
              {chosen !== undefined && hasAnswerKey(current) && (
                <p className="gp-outcome">
                  {sameAnswer(chosen, current.correctAnswer)
                    ? `The chosen answer (${chosen}) is correct.`
                    : `The chosen answer (${chosen}) is not correct.`}
                </p>
              )}
              {hasAnswerKey(current) ? (
                <p className="gp-answer" data-testid="gp-answer">
                  Correct answer: <strong>{current.correctAnswer}</strong>
                </p>
              ) : (
                <p className="gp-answer" data-testid="gp-answer">
                  The answer key for this question isn't available, so no choice is marked right or wrong.
                </p>
              )}
              <Suspense fallback={<p className="gp-hint">Loading explanation…</p>}>
                <DetailedGuidanceContent
                  briefExplanation={current.briefExplanation}
                  additionalExplanation={current.additionalExplanation}
                  refLinks={current.refLinks}
                />
              </Suspense>
              {!preloaded && <div className="gp-coverage">
                <Button variant="outlined" color="primary" size="small"
                  disabled={coverage[current.id] === "saving" || coverage[current.id] === "saved"}
                  onClick={() => markCovered(current.id)}>
                  {coverage[current.id] === "saved" ? `Covered with ${who}` : coverage[current.id] === "saving" ? "Saving coverage…" : "Mark covered together"}
                </Button>
                {coverage[current.id] === "error" && <p className="gp-error" role="alert">Couldn't save coverage. Mark covered again to retry.</p>}
              </div>}
              {!current.briefExplanation && !current.additionalExplanation && (
                <p className="gp-hint">No explanation is available for this question.</p>
              )}
            </section>
          )}

          <div className="gp-actions gp-nav">
            <Button
              size="small"
              variant="outlined"
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              disabled={index === 0}
              aria-label="Previous question"
            >
              Previous
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setIndex((i) => Math.min(questions.length - 1, i + 1))}
              disabled={index >= questions.length - 1}
              aria-label="Next question"
            >
              Next
            </Button>
            <Button size="small" onClick={onReset} aria-label="Reset practice">Reset</Button>
            {!preloaded && (
              <Button size="small" onClick={clearSession} aria-label="New questions">New questions</Button>
            )}
            {!preloaded && <Button size="small" onClick={onEnd} aria-label="End practice">End</Button>}
            {preloaded && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={onFinishClick}
                disabled={finishing || Object.keys(selections).length === 0}
                data-testid="gp-finish"
              >
                {finishing ? <CircularProgress size={18} color="inherit" /> : `Finish and save for ${who}`}
              </Button>
            )}
          </div>
          {preloaded && finishError && (
            <div className="gp-error" role="alert" data-testid="gp-finish-error">
              <span>{finishError}</span>{" "}
              <Button size="small" variant="outlined" onClick={onFinishClick} disabled={finishing}>
                Retry
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
